import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, X, Crown, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import api from '../utils/api';

const FamilyPlanManager = () => {
  const [members, setMembers] = useState([]);
  const [isFamilyOwner, setIsFamilyOwner] = useState(false);
  const [maxMembers, setMaxMembers] = useState(5);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [userFamilies, setUserFamilies] = useState([]);
  const [showFamilySelector, setShowFamilySelector] = useState(false);
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [pendingMemberEmail, setPendingMemberEmail] = useState('');

  useEffect(() => {
    fetchFamilyMembers();
  }, []);

  const fetchFamilyMembers = async () => {
    try {
      const response = await api.get('/subscription/family-members');
      setMembers(response.data.members || []);
      setIsFamilyOwner(response.data.is_family_owner || false);
      setMaxMembers(response.data.max_members || 5);
    } catch (error) {
      console.error('Error fetching family members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      toast.error(t('family.enterValidEmail'));
      return;
    }

    // First, check user's families
    try {
      const familiesResponse = await api.get('/family/');
      const families = familiesResponse.data.families || [];
      setUserFamilies(families);
      
      if (families.length === 0) {
        // No family - will create automatically
        proceedWithAddMember(newMemberEmail, null);
      } else if (families.length === 1) {
        // One family - add automatically
        proceedWithAddMember(newMemberEmail, families[0].id);
      } else {
        // Multiple families - ask user to choose
        setPendingMemberEmail(newMemberEmail);
        setShowFamilySelector(true);
      }
    } catch (error) {
      console.error('Error checking families:', error);
      toast.error(t('family.errorCheckingFamilies'));
    }
  };

  const proceedWithAddMember = async (email, familyId) => {
    try {
      setAdding(true);
      const response = await api.post('/subscription/family-members/add', {
        email: email,
        family_id: familyId
      });
      
      toast.success(response.data.message);
      setNewMemberEmail('');
      setShowFamilySelector(false);
      await fetchFamilyMembers();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.detail || t('family.errorAddingMember'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm(t('family.confirmRemoveMember'))) {
      return;
    }

    try {
      await api.delete(`/subscription/family-members/${memberId}`);
      toast.success(t('family.memberRemoved'));
      await fetchFamilyMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(t('family.errorRemovingMember'));
    }
  };

  if (loading) {
    return (
      <Card className="p-6 border-2 border-amber-100">
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-orange-600 border-t-transparent"></div>
        </div>
      </Card>
    );
  }

  if (!isFamilyOwner) {
    return null; // Don't show if not family owner
  }

  const remainingSlots = maxMembers - members.length;

  return (
    <Card className="p-6 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-green-600" />
          <h3 className="text-xl font-semibold text-green-900">
            {t('family.familyPlanMembers')}
          </h3>
        </div>
        <Badge className="bg-green-600 text-white">
          {members.length}/{maxMembers} {t('family.membersCount')}
        </Badge>
      </div>

      {/* Add Member Form */}
      {remainingSlots > 0 && (
        <div className="mb-6 p-4 bg-white border-2 border-green-200 rounded-lg">
          <p className="text-sm font-semibold text-green-900 mb-3">
            ➕ {t('family.addMember')} ({remainingSlots} {remainingSlots === 1 ? t('family.slot') : t('family.remainingSlots')})
          </p>
          <div className="flex gap-2">
            <Input
              type="email"
              value={newMemberEmail}
              onChange={(e) => setNewMemberEmail(e.target.value)}
              placeholder={t('family.emailPlaceholder')}
              className="flex-1"
              disabled={adding}
            />
            <Button
              onClick={handleAddMember}
              disabled={adding || !newMemberEmail}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {adding ? t('family.adding') : t('family.add')}
            </Button>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            💡 {t('family.userMustHaveAccount')}
          </p>
        </div>
      )}

      {remainingSlots === 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            ⚠️ {t('family.limitReached')}
          </p>
        </div>
      )}

      {/* Members List */}
      <div className="space-y-3">
        {members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>{t('family.noMembersYet')}</p>
            <p className="text-sm mt-1">{t('family.addUpTo5')}</p>
          </div>
        ) : (
          members.map((member) => (
            <div
              key={member.user_id}
              className={`flex items-center justify-between p-4 border rounded-lg transition-shadow ${
                member.is_owner 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-md' 
                  : 'bg-white border-green-200 hover:shadow-md'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  member.is_owner ? 'bg-green-200' : 'bg-green-100'
                }`}>
                  <Crown className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{member.name}</p>
                    {member.is_owner && (
                      <Badge className="bg-green-600 text-white text-xs">
                        {t('family.you')} ({t('family.owner')})
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{member.email}</p>
                  <p className="text-xs text-gray-500">
                    {member.is_owner 
                      ? t('family.planOwner')
                      : `${t('family.addedOn')} ${new Date(member.added_at).toLocaleDateString('pt-BR')}`
                    }
                  </p>
                </div>
              </div>
              {!member.is_owner && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveMember(member.user_id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))
        )}
      </div>
      
      {/* Family Selector Modal */}
      <Dialog open={showFamilySelector} onOpenChange={setShowFamilySelector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('family.chooseFamily')}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              {t('family.chooseFamilyDescription').replace('{count}', userFamilies.length)} <strong>{pendingMemberEmail}</strong>?
            </p>
            
            <div className="space-y-2">
              {userFamilies.map((family) => (
                <Button
                  key={family.id}
                  onClick={() => {
                    setSelectedFamilyId(family.id);
                    proceedWithAddMember(pendingMemberEmail, family.id);
                  }}
                  variant="outline"
                  className="w-full justify-start hover:bg-orange-50 border-orange-200"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {family.name} ({family.member_count || 0} {t('family.membersCount')})
                </Button>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowFamilySelector(false);
                setPendingMemberEmail('');
              }}
            >
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FamilyPlanManager;
